<script setup>
import {
  onMounted,
  computed
} from 'vue'
import { useStore } from 'vuex'
import { useLoginModal } from '@/components/Login'
import HelpCenterAnimate from './HelpCenterAnimate/index.vue'
import { fetchData } from '@/utils/fetch'
import { useRoute } from 'vue-router'
import {
  buriedPointUtil,
  gaAnalytics
} from '@/utils/utils'
import { useUserStore } from '@/pinia/user'
import Popover from '@/components/Popover/index.vue'
import Personal from './Personal/index.vue'
import QuickMenu from './QuickMenu/index.vue'
import Icon from '../Icon/Icon.vue'
import HeaderSearch from './HeaderSearch/index.vue'
import { PageContainer } from '../PageLayout'

const route = useRoute()
const store = useStore()

const { open: openLoginModal } = useLoginModal()

const props = defineProps({
  guidancePopVisible: {
    type: Boolean,
    required: false
  },
  showCeiling: {
    type: Boolean,
    default: false
  }
})

const user = useUserStore()

const userInfo = computed(() => user.userInfo || {})

const showHelpCenterAnimate = computed(() => store.state.common.showHelpCenterAnimate)

const showBanner = computed(() => route.path === '/' && !props.showCeiling)

const maskEmail = computed(() => {
  if (!userInfo?.value?.email) {
    return ''
  }
  const [username, domain] = userInfo?.value?.email?.split('@')
  return username.slice(0, 3) + '***' + username.slice(-3) + '@' + domain
})

const handleLogin = () => {
  if (user.isLogged) return

  gaAnalytics('p_sign_in_click', 'p_sign_in_click')
  buriedPointUtil({ eventId: 'PWH201' })
  store.commit('setPassiveLogin', 0)

  openLoginModal()
}

onMounted(() => {
  getUserInfo()
})

const getUserInfo = async () => {
  const { code, data } = await fetchData('/dsp-app/alibaba-inter/queryMyInfo', {
    method: 'post'
  })
  if (code === 200) {
    const completeIds = data?.complete || []
    const complete = localStorage.getItem('complete') || ''

    const strNumbers = complete.split(',').map(num => parseFloat(num)).filter(num => !isNaN(num)) || []
    // 过滤出不在数组中的数字
    const result = strNumbers.filter(num => !completeIds.includes(num))
    if (result.length) {
      const { code } = await fetchData('/dsp-app/alibaba-inter/userComplete', {
        method: 'PUT',
        body: JSON.stringify(result)
      })
      if (code === 200) {
        const completeStr = [...result, ...completeIds].join(',')
        localStorage.setItem('complete', completeStr)
      }
    } else {
      localStorage.setItem('complete', completeIds.join(','))
    }
  }
}

const goHome = () => {
  location.href = '/'
}
</script>

<template>
  <PageContainer
    :class="{ 'mini-mode': !showBanner }"
    class="search-main"
  >
    <div
      v-if="showBanner"
      class="banner"
    />
    <div class="search-container">
      <Icon
        class="search-container-logo"
        name="bestdealer-logo"
        @click="goHome"
      />
      <Icon
        class="search-container-logo--b"
        name="bestdealer-b-l"
        @click="goHome"
      />
      <HeaderSearch />
      <QuickMenu />
      <div class="split-line-wrapper">
        <div class="split-line" />
      </div>

      <!-- 个人中心 -->
      <Popover
        :disabled="!userInfo?.name"
        placement="bottom"
        trigger="hover"
      >
        <template #reference>
          <div
            class="search-person"
            :class="{ showPerson: showHelpCenterAnimate }"
            @click="handleLogin"
          >
            <div
              class="person-left"
              :class="{ personLeftZoom: showHelpCenterAnimate }"
            >
              <Icon :name="userInfo?.name ? 'person-logined' : 'person-unlogin'" />
            </div>
            <div class="person-right">
              <div
                v-if="!userInfo?.name"
                class="unlogin-text"
              >
                <div>Hello, Sign in</div>
                <div>Account</div>
              </div>
              <div
                v-else
                class="login-text"
              >
                <div>Hello,</div>
                <div class="mask-email">
                  {{ maskEmail }}
                </div>
              </div>
            </div>
          </div>
        </template>
        <Personal />
      </Popover>
      <HelpCenterAnimate v-if="showHelpCenterAnimate" />
    </div>
  </PageContainer>
</template>

<style scoped lang="less">
.search-main {
  width: 100%;
  height: 192px;
  background: url('@/assets/header/pc-header-banner.png') no-repeat center center / cover;
  &.mini-mode {
    height: 72px;
    background: #052784;
  }
  .banner {
    height: 120px;
  }
}

.search-main .search-container {
  display: flex;
  align-items: center;
  height: 72px;

  .search-container-logo {
    margin-right: 24px;
    width: 200px;
    height: 40px;
    color: #FFFFFF;
    display: none;
    cursor: pointer;

    @media (min-width: 1024px) {
      display: block;
    }

    &--b {
      flex-shrink: 0;
      margin-right: 12px;
      font-size: 32px;
      color: #FFFFFF;
      display: block;
      cursor: pointer;

      @media (min-width: 1024px) {
        display: none;
      }
    }
  }

  .split-line-wrapper {
    display: flex;
    align-items: center;
    height: 40px;

    .split-line {
      height: 20px;
      width: 1px;
      background-color: #3254B3;
      margin: 0 16px;
    }
  }
}

.search-country {
  margin-left: auto;
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 98px;
  margin-right: 16px;
}

.search-country .country-left {
  width: 40px;
  height: 40px;
  overflow: hidden;

  display: flex;
  align-items: center;
  justify-content: center;

  img {
    padding: 4px;
    width: 100%;
    object-fit: cover;
    display: flex;
  }
}

.search-country .country-right {
  cursor: pointer;
}

.search-country .country-right .country-text {
  font-family: mon-500;
  font-size: 12px;
  line-height: 18px;
  color: #ffffff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.search-country .country-right .country-unit {
  font-family: mon-600;
  font-size: 12px;
  line-height: 18px;
  color: #ffffff;
}

.search-country .country-right .country-unit img {
  vertical-align: middle;
  margin-left: 4px;
}

.search-person {
  position: relative;
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;

  .person-left {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    font-size: 32px;
    color: white;
  }

  .person-right {
    width: 106px;
    display: none;

    @media (min-width: 1024px) {
      display: block;
    }

    .unlogin-text, .login-text {
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: 4px;
      color: white;
      font-size: 12px;
      font-family: mon-500;
      height: 40px;
    }
    .mask-email {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .personLeftZoom {
    animation: zoomInOut 0.8s ease-in-out 0.4s infinite;
  }
}

@keyframes zoomInOut {
  0% {
    transform: scale(1);
    /* 初始大小 */
  }

  50% {
    transform: scale(1.2);
    /* 放大到1.5倍 */
  }

  100% {
    transform: scale(1);
    /* 缩小回原始大小 */
  }
}

.showPerson {
  z-index: 1200;
}
</style>
